import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiErrorMessage } from '@/api/client';
import {
  listCollectionLinks,
  createCollectionLink,
  revokeCollectionLink,
  type CollectionLink,
  type CollectionLinkFilter,
} from '@/api/collectionLinks';
import FilterBar from '@/components/common/FilterBar';
import { useListFilter } from '@/hooks/useListFilter';
import { isAdminOrFinance } from '@/domain/roles';
import { useSessionStore } from '@/store/session';

const { Paragraph, Text } = Typography;

/**
 * 常用币种。留空表示让填写人自己选。
 *
 * 带上中文名：`SGD` / `HKD` / `AUD` 三个代码长得像，光看字母很容易选错，而这里选错的
 * 代价是整张表单要填的字段全变了（币种决定表单结构，见 PRODUCT.md §4.1），
 * 对方填完才发现不对。
 *
 * 🔴 `value` 必须是三位代码本身 —— 它要原样发给 Airwallex，混进中文就是一张废单。
 */
const CURRENCIES: Array<{ code: string; name: string }> = [
  { code: 'USD', name: '美元' },
  { code: 'EUR', name: '欧元' },
  { code: 'GBP', name: '英镑' },
  { code: 'SGD', name: '新加坡元' },
  { code: 'HKD', name: '港元' },
  { code: 'JPY', name: '日元' },
  { code: 'AUD', name: '澳元' },
  { code: 'CNY', name: '人民币' },
];

const STATUS: Record<CollectionLink['status'], { label: string; color: string }> = {
  PENDING: { label: '待填写', color: 'gold' },
  FILLED: { label: '已填写', color: 'green' },
  REVOKED: { label: '已作废', color: 'default' },
};

const STATUS_OPTIONS = (Object.keys(STATUS) as CollectionLink['status'][]).map((value) => ({
  value,
  label: STATUS[value].label,
}));

/**
 * 有效性。**只对「待填写」的链接有意义** —— 已填写的链接过没过期都不再是一把能用的钥匙。
 * 服务端对这两个取值都隐含了 `status = PENDING`。
 */
const VALIDITY_OPTIONS = [
  { value: 'ACTIVE', label: '生效中（待填写）' },
  { value: 'EXPIRED', label: '已过期（待填写）' },
];

const VERIFICATION_OPTIONS = [
  { value: true, label: '需邮箱验证' },
  { value: false, label: '无需验证' },
];

/**
 * 收款信息收集链接。发给**组织外部**的人，让他自己填收款信息。
 *
 * 填上来的信息会变成一个草稿收款人，与站内手动添加完全一致 —— 不推送 Airwallex，
 * 直到第一次打款前才创建 beneficiary。
 */
export default function CollectionLinks() {
  const { message } = App.useApp();
  const me = useSessionStore((s) => s.me);
  // member 能看这一页，但只能看：写操作是 admin / finance 的活（服务端同一条判定）。
  const canManage = isAdminOrFinance(me?.roles ?? []);

  const [rows, setRows] = useState<CollectionLink[]>([]);
  const [loading, setLoading] = useState(false);
  const f = useListFilter<CollectionLinkFilter>();
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CollectionLink | null>(null);
  const [form] = Form.useForm<{
    title: string;
    requireEmailVerification: boolean;
    requiredEmail?: string;
    currency?: string;
    ttlDays?: number;
  }>();
  /** 开关状态要驱动邮箱字段的必填与显隐，所以得订阅它 */
  const needEmail = Form.useWatch('requireEmailVerification', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listCollectionLinks(f.filter));
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, f.filter]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      message.success('链接已复制');
    } catch {
      // clipboard 在非 https / 无权限时会抛，给个能手动复制的兜底
      message.warning('复制失败，请手动选中链接');
    }
  }

  async function submit() {
    let values: {
      title: string;
      requireEmailVerification: boolean;
      requiredEmail?: string;
      currency?: string;
      ttlDays?: number;
    };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const link = await createCollectionLink({
        title: values.title.trim(),
        requireEmailVerification: values.requireEmailVerification,
        requiredEmail: values.requiredEmail?.trim() || undefined,
        currency: values.currency || undefined,
        ttlDays: values.ttlDays,
      });
      setCreating(false);
      form.resetFields();
      // 创建完立刻把链接摆出来 —— 这是创建这件事的唯一产出，不该让人再去列表里找
      setCreated(link);
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function revoke(row: CollectionLink) {
    try {
      await revokeCollectionLink(row.id);
      message.success('已作废');
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    }
  }

  return (
    <Card
      title="收款信息收集"
      extra={
        canManage ? (
          <Button type="primary" onClick={() => setCreating(true)}>
            新建链接
          </Button>
        ) : null
      }
    >
      <Paragraph type="secondary">
        生成一个链接发给对方，让他自己填收款信息。填好的信息会变成一个<Text strong>草稿收款人</Text>，
        与手动添加一样：只有在第一次打款前才会真正创建到空中云汇。
      </Paragraph>

      <FilterBar active={f.active} resultCount={rows.length} onReset={f.reset}>
        <Input
          allowClear
          style={{ width: 220 }}
          placeholder="搜标题 / 邮箱 / 收款人"
          value={f.keyword}
          onChange={(e) => f.setKeyword(e.target.value)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 130 }}
          placeholder="状态"
          options={STATUS_OPTIONS}
          value={f.filter.status ?? []}
          onChange={(v: CollectionLink['status'][]) => f.set('status', v)}
        />
        <Select
          allowClear
          style={{ minWidth: 170 }}
          placeholder="有效性"
          options={VALIDITY_OPTIONS}
          value={f.filter.validity}
          onChange={(v?: 'ACTIVE' | 'EXPIRED') => f.set('validity', v)}
        />
        <Select
          allowClear
          style={{ minWidth: 150 }}
          placeholder="邮箱验证"
          options={VERIFICATION_OPTIONS}
          value={f.filter.requireEmailVerification}
          onChange={(v?: boolean) => f.set('requireEmailVerification', v)}
        />
        <DatePicker.RangePicker
          placeholder={['创建起', '创建止']}
          value={f.range}
          onChange={f.setRange}
        />
      </FilterBar>

      <Table<CollectionLink>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: '标题',
            dataIndex: 'title',
            width: 200,
            render: (title: string, row) => (
              <Space direction="vertical" size={0}>
                <Text strong>{title}</Text>
                <Space size={4} wrap>
                  {row.requireEmailVerification ? (
                    <Tooltip title={`验证码只发往 ${row.requiredEmail}`}>
                      <Tag>需邮箱验证</Tag>
                    </Tooltip>
                  ) : (
                    <Tooltip title="任何拿到链接的人都能提交">
                      <Tag color="orange">无需验证</Tag>
                    </Tooltip>
                  )}
                  {row.currency && <Tag color="blue">{row.currency}</Tag>}
                </Space>
                {row.requiredEmail && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.requiredEmail}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (s: CollectionLink['status']) => (
              <Tag color={STATUS[s].color}>{STATUS[s].label}</Tag>
            ),
          },
          {
            title: '填写情况',
            width: 220,
            render: (_, row) =>
              row.status === 'FILLED' ? (
                <Space direction="vertical" size={0}>
                  <Text>{row.payeeName ?? '已提交'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.verifiedEmail ?? '未验证邮箱'} ·{' '}
                    {row.filledAt ? dayjs(row.filledAt).format('MM-DD HH:mm') : ''}
                  </Text>
                </Space>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: '有效期至',
            dataIndex: 'expiresAt',
            width: 140,
            render: (v: string) => {
              const expired = dayjs(v).isBefore(dayjs());
              return (
                <Text type={expired ? 'danger' : undefined}>
                  {dayjs(v).format('YYYY-MM-DD')}
                  {expired && '（已过期）'}
                </Text>
              );
            },
          },
          { title: '创建人', dataIndex: 'createdByName', width: 90 },
          {
            title: '操作',
            width: 160,
            render: (_, row) => (
              <Space>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copy(row.url)}
                >
                  复制链接
                </Button>
                {canManage && row.status === 'PENDING' && (
                  <Popconfirm
                    title="作废这个链接？"
                    description="作废后对方打开将无法填写。"
                    onConfirm={() => revoke(row)}
                  >
                    <Button type="link" size="small" danger>
                      作废
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={creating}
        title="新建收款信息收集链接"
        okText="生成链接"
        confirmLoading={submitting}
        onCancel={() => setCreating(false)}
        onOk={submit}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ requireEmailVerification: true, ttlDays: 30 }}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }, { max: 200 }]}
            extra="对方打开链接时会看到这句话，写清楚你是谁、为什么要他的收款信息。"
          >
            <Input autoFocus placeholder="例如：2026 年 9 月稿费收款信息" />
          </Form.Item>

          <Form.Item
            name="requireEmailVerification"
            label="需要邮箱验证"
            valuePropName="checked"
            extra="开启后，对方要先收验证码才能填写。关闭意味着任何拿到链接的人都能提交。"
          >
            <Switch />
          </Form.Item>

          {needEmail && (
            <Form.Item
              name="requiredEmail"
              label="收件邮箱"
              rules={[
                { required: true, message: '请输入收件邮箱' },
                { type: 'email', message: '邮箱格式不正确' },
              ]}
              extra="验证码只会发到这个地址，对方无法改成别的邮箱。请确认填的是本人邮箱。"
            >
              <Input placeholder="对方的邮箱，例如 someone@example.com" />
            </Form.Item>
          )}

          <Form.Item
            name="currency"
            label="指定收款币种"
            extra="留空则由对方自己选。指定后对方不必也不能再改——币种决定了整张表单要填哪些字段。"
          >
            <Select
              showSearch
              // 输入了却不过滤，会让人以为下拉坏了，然后盲选到错误的币种
              optionFilterProp="label"

              allowClear
              placeholder="不指定（由对方选择）"
              // label 带全称，所以 optionFilterProp="label" 让「singapore」也能搜到 ——
              // 记不住代码的人正是要靠全称找
              options={CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))}
            />
          </Form.Item>

          <Form.Item
            name="ttlDays"
            label="有效天数"
            rules={[{ type: 'number', min: 1, max: 365 }]}
            extra="链接会被转发、被留在聊天记录里，不该永久有效。"
          >
            <InputNumber min={1} max={365} style={{ width: '100%' }} addonAfter="天" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建成功后单独弹一次链接：这是整个操作的产出，必须让人一眼拿到 */}
      <Modal
        open={Boolean(created)}
        title="链接已生成"
        onCancel={() => setCreated(null)}
        footer={
          <Space>
            <Button onClick={() => setCreated(null)}>关闭</Button>
            <Button type="primary" icon={<CopyOutlined />} onClick={() => created && copy(created.url)}>
              复制链接
            </Button>
          </Space>
        }
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          title="这个链接本身就是凭据"
          description={
            created?.requireEmailVerification
              ? '拿到链接的人还需要通过邮箱验证才能提交，但仍请通过可信渠道发送。'
              : '这个链接没有开启邮箱验证，任何拿到它的人都能提交收款信息。请只发给本人。'
          }
        />
        <Paragraph copyable={{ text: created?.url, icon: <LinkOutlined /> }}>
          <Text code style={{ wordBreak: 'break-all' }}>
            {created?.url}
          </Text>
        </Paragraph>
      </Modal>
    </Card>
  );
}
